import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLang } from '../../i18n/LangContext';
import { SERVICE_CATEGORIES } from '../../data/siteData';
import { ArrowRight, Upload, X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { appendAttribution } from '../../utils/leadTracking';
import { getEstimateService, clearEstimateService } from '../../utils/estimatePreselect';
import { trackEvent } from '../../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LeadForm() {
  const { lang, t } = useLang();
  const lf = t.leadForm;
  const [form, setForm] = useState({
    name: '', phone: '', email: '', address: '', city: '', zip: '',
    services: [], budget: '', timeline: '', description: '', contactMethod: lf.contacts[0],
  });
  const [preselectPage, setPreselectPage] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  // Preselect a service when the visitor arrived from a service page CTA.
  const applyPreselect = useCallback(() => {
    const pre = getEstimateService();
    if (!pre || !pre.category) return;
    setPreselectPage(pre.page || '');
    setForm(p => {
      const services = p.services.includes(pre.category) ? p.services : [...p.services, pre.category];
      const line = (lang === 'es' ? 'Servicio de interés' : 'Service of interest') + `: ${pre.detail || pre.category}`;
      const description = p.description ? p.description : line;
      return { ...p, services, description };
    });
    clearEstimateService();
  }, [lang]);

  useEffect(() => {
    applyPreselect();
    window.addEventListener('mmt:estimate-preselect', applyPreselect);
    return () => window.removeEventListener('mmt:estimate-preselect', applyPreselect);
  }, [applyPreselect]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleService = (s) => set('services', form.services.includes(s) ? form.services.filter(x => x !== s) : [...form.services, s]);

  const handleFiles = (e) => {
    const newFiles = Array.from(e.target.files || []);
    const maxSize = 25 * 1024 * 1024;
    const valid = newFiles.filter(f => f.size <= maxSize);
    setFiles(prev => [...prev, ...valid]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) { toast.error(lf.required); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => formData.append(k, Array.isArray(v) ? JSON.stringify(v) : v));
      formData.append('language', lang);
      appendAttribution(formData);
      files.forEach(f => formData.append('files', f));
      const res = await fetch(`${API}/api/leads`, { method: 'POST', body: formData });
      if (!res.ok) throw new Error('Failed');
      trackEvent('form_submit', { form: 'lead', services: form.services.join(', '), service_page: preselectPage || window.location.pathname });
      toast.success(lf.success);
      setForm({ name: '', phone: '', email: '', address: '', city: '', zip: '', services: [], budget: '', timeline: '', description: '', contactMethod: lf.contacts[0] });
      setFiles([]);
      clearEstimateService();
      setPreselectPage('');
    } catch { toast.error(lf.error); }
    finally { setLoading(false); }
  };

  const serviceCats = SERVICE_CATEGORIES.map(c => {
    const loc = t.services.cats[c.slug];
    return loc ? loc.title : c.title;
  });

  return (
    <section id="estimate" className="bg-[#0B0D0F] py-20 sm:py-28">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-[#D4AF37] text-xs font-mono tracking-[0.25em] uppercase">{lf.label}</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-3 font-outfit">{lf.heading}</h2>
          <p className="text-slate-400 mt-4">{lf.desc}</p>
        </div>
        <form onSubmit={handleSubmit} data-testid="lead-capture-form" className="bg-[#12151A] rounded-lg border border-white/8 p-6 sm:p-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div><label className="text-slate-400 text-xs mb-1.5 block">{lf.name} *</label><input data-testid="lead-input-name" required value={form.name} onChange={e => set('name', e.target.value)} className="w-full bg-[#0B0D0F] border border-white/10 rounded px-4 py-3 text-white text-sm focus:border-[#D4AF37]/50 focus:outline-none" /></div>
            <div><label className="text-slate-400 text-xs mb-1.5 block">{lf.phone} *</label><input data-testid="lead-input-phone" required value={form.phone} onChange={e => set('phone', e.target.value)} className="w-full bg-[#0B0D0F] border border-white/10 rounded px-4 py-3 text-white text-sm focus:border-[#D4AF37]/50 focus:outline-none" /></div>
            <div><label className="text-slate-400 text-xs mb-1.5 block">{lf.email} *</label><input data-testid="lead-input-email" type="email" required value={form.email} onChange={e => set('email', e.target.value)} className="w-full bg-[#0B0D0F] border border-white/10 rounded px-4 py-3 text-white text-sm focus:border-[#D4AF37]/50 focus:outline-none" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div><label className="text-slate-400 text-xs mb-1.5 block">{lf.address}</label><input value={form.address} onChange={e => set('address', e.target.value)} className="w-full bg-[#0B0D0F] border border-white/10 rounded px-4 py-3 text-white text-sm focus:border-[#D4AF37]/50 focus:outline-none" /></div>
            <div><label className="text-slate-400 text-xs mb-1.5 block">{lf.city}</label><input value={form.city} onChange={e => set('city', e.target.value)} className="w-full bg-[#0B0D0F] border border-white/10 rounded px-4 py-3 text-white text-sm focus:border-[#D4AF37]/50 focus:outline-none" /></div>
            <div><label className="text-slate-400 text-xs mb-1.5 block">{lf.zip}</label><input value={form.zip} onChange={e => set('zip', e.target.value)} className="w-full bg-[#0B0D0F] border border-white/10 rounded px-4 py-3 text-white text-sm focus:border-[#D4AF37]/50 focus:outline-none" /></div>
          </div>
          <div className="mb-6">
            <label className="text-slate-400 text-xs mb-2 block">{lf.servicesReq}</label>
            <div className="flex flex-wrap gap-2">
              {serviceCats.map((title, i) => (
                <button key={i} type="button" onClick={() => toggleService(SERVICE_CATEGORIES[i].title)}
                  className={`px-3 py-1.5 rounded border text-xs transition-all ${form.services.includes(SERVICE_CATEGORIES[i].title) ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-white/10 text-slate-400 hover:border-white/20'}`}>{title}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div><label className="text-slate-400 text-xs mb-1.5 block">{lf.budget}</label>
              <select data-testid="lead-select-budget" value={form.budget} onChange={e => set('budget', e.target.value)} className="w-full bg-[#0B0D0F] border border-white/10 rounded px-4 py-3 text-white text-sm focus:border-[#D4AF37]/50 focus:outline-none">
                <option value="">{lf.select}</option>{lf.budgets.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div><label className="text-slate-400 text-xs mb-1.5 block">{lf.timeline}</label>
              <select value={form.timeline} onChange={e => set('timeline', e.target.value)} className="w-full bg-[#0B0D0F] border border-white/10 rounded px-4 py-3 text-white text-sm focus:border-[#D4AF37]/50 focus:outline-none">
                <option value="">{lf.select}</option>{lf.timelines.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div><label className="text-slate-400 text-xs mb-1.5 block">{lf.contactPref}</label>
              <select value={form.contactMethod} onChange={e => set('contactMethod', e.target.value)} className="w-full bg-[#0B0D0F] border border-white/10 rounded px-4 py-3 text-white text-sm focus:border-[#D4AF37]/50 focus:outline-none">
                {lf.contacts.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
          <div className="mb-6"><label className="text-slate-400 text-xs mb-1.5 block">{lf.description}</label>
            <textarea data-testid="lead-textarea-details" rows={4} value={form.description} onChange={e => set('description', e.target.value)} className="w-full bg-[#0B0D0F] border border-white/10 rounded px-4 py-3 text-white text-sm focus:border-[#D4AF37]/50 focus:outline-none resize-none" />
          </div>
          <div className="mb-8">
            <label className="text-slate-400 text-xs mb-2 block">{lf.uploadLabel}</label>
            <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/jpg,image/heic,application/pdf" onChange={handleFiles} className="hidden" />
            <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 border border-dashed border-white/20 hover:border-[#D4AF37]/30 rounded px-4 py-3 text-slate-400 text-sm transition-colors">
              <Upload className="w-4 h-4" />{lf.uploadHint}
            </button>
            {files.length > 0 && (
              <div className="mt-3 space-y-2">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#0B0D0F] rounded px-3 py-2">
                    <div className="flex items-center gap-2 text-sm text-slate-300 truncate"><FileText className="w-4 h-4 text-[#D4AF37] flex-shrink-0" /><span className="truncate">{f.name}</span><span className="text-slate-500 text-xs flex-shrink-0">({(f.size / 1024 / 1024).toFixed(1)}MB)</span></div>
                    <button type="button" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))} className="text-slate-500 hover:text-red-400"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button type="submit" disabled={loading} data-testid="lead-submit-button"
            className="w-full group flex items-center justify-center gap-3 bg-[#D4AF37] hover:bg-[#E5C158] disabled:opacity-50 text-black font-bold py-4 rounded text-base transition-all">
            {loading ? lf.submitting : lf.submit}{!loading && <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />}
          </button>
        </form>
      </div>
    </section>
  );
}
